/**
 * 岗位表单对话框
 */
import React, { useEffect } from 'react';
import { Modal, Form, Input, Select, InputNumber } from 'antd';
import { message } from '@/utils/antdStatic';
import { createPosition, updatePosition } from '../../api/position';
import type { PositionInfo, PositionFormData } from '../../api/types';

interface PositionFormProps {
  visible: boolean;
  position: PositionInfo | null;
  categories: { value: string; label: string }[];
  onCancel: () => void;
  onSuccess: () => void;
}

const PositionForm: React.FC<PositionFormProps> = ({
  visible,
  position,
  categories,
  onCancel,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const isEdit = !!position;

  useEffect(() => {
    if (visible && position) {
      form.setFieldsValue({
        name: position.name,
        code: position.code,
        category: position.category,
        level: position.level,
        description: position.description,
        requirements: position.requirements,
        sort: position.sort,
        status: position.status,
      });
    } else if (visible) {
      form.resetFields();
      form.setFieldsValue({
        status: 1,
        sort: 0,
      });
    }
  }, [visible, position, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const data: PositionFormData = {
        name: values.name,
        code: values.code,
        category: values.category,
        level: values.level,
        description: values.description,
        requirements: values.requirements,
        sort: values.sort,
        status: values.status,
      };

      if (isEdit) {
        await updatePosition(position!.id, data);
        message.success('更新成功');
      } else {
        await createPosition(data);
        message.success('创建成功');
      }

      onSuccess();
    } catch (error) {
      console.error('提交失败', error);
    }
  };

  return (
    <Modal
      title={isEdit ? '编辑岗位' : '新增岗位'}
      open={visible}
      onOk={handleSubmit}
      onCancel={onCancel}
      width={600}
      destroyOnHidden
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          status: 1,
          sort: 0,
        }}
      >
        <Form.Item
          name="name"
          label="岗位名称"
          rules={[{ required: true, message: '请输入岗位名称' }]}
        >
          <Input placeholder="请输入岗位名称" maxLength={50} />
        </Form.Item>

        <Form.Item
          name="code"
          label="岗位编码"
          rules={[
            { required: true, message: '请输入岗位编码' },
            { pattern: /^[A-Za-z0-9_-]+$/, message: '只能包含字母、数字、下划线和连字符' },
          ]}
        >
          <Input placeholder="请输入岗位编码" maxLength={50} disabled={isEdit} />
        </Form.Item>

        <Form.Item
          name="category"
          label="岗位类别"
          rules={[{ required: true, message: '请选择岗位类别' }]}
        >
          <Select placeholder="请选择岗位类别" options={categories} />
        </Form.Item>

        <Form.Item
          name="level"
          label="岗位级别"
        >
          <InputNumber min={1} max={20} placeholder="请输入岗位级别" style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item
          name="description"
          label="岗位描述"
        >
          <Input.TextArea
            placeholder="请输入岗位描述"
            rows={3}
            maxLength={500}
            showCount
          />
        </Form.Item>

        <Form.Item
          name="requirements"
          label="任职要求"
        >
          <Input.TextArea
            placeholder="请输入任职要求"
            rows={3}
            maxLength={500}
            showCount
          />
        </Form.Item>

        <Form.Item
          name="sort"
          label="排序"
        >
          <InputNumber min={0} max={9999} style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item
          name="status"
          label="状态"
          rules={[{ required: true, message: '请选择状态' }]}
        >
          <Select placeholder="请选择状态">
            <Select.Option value={1}>启用</Select.Option>
            <Select.Option value={0}>禁用</Select.Option>
          </Select>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default PositionForm;
